// CSRF Token Management Utilities
// This file provides CSRF token handling functions that can be used across the application

// Function to refresh CSRF token
export function refreshCSRFToken() {
  return fetch('/csrf_token', {
    method: 'GET',
    credentials: 'same-origin'
  })
  .then(response => response.json())
  .then(data => {
    if (data.csrf_token) {
      const metaTag = document.querySelector('meta[name="csrf-token"]');
      if (metaTag) {
        metaTag.setAttribute('content', data.csrf_token);
      }
      return data.csrf_token;
    }
    throw new Error('No CSRF token received');
  })
  .catch(error => {
    window.rollbar.error('Error refreshing CSRF token:', error);
    throw error;
  });
}

// Function to get current CSRF token
export function getCSRFToken() {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  return metaTag ? metaTag.getAttribute('content') : null;
}

// Enhanced fetch wrapper with CSRF token handling
export function fetchWithCSRF(url, options = {}) {
  const token = getCSRFToken();

  if (!token) {
    // If no token, try to refresh it first
    return refreshCSRFToken().then(() => {
      const newToken = getCSRFToken();
      if (newToken) {
        return performFetch(url, options, newToken);
      } else {
        throw new Error('Unable to obtain CSRF token');
      }
    });
  }

  return performFetch(url, options, token);
}

function performFetch(url, options, token) {
  const fetchOptions = {
    ...options,
    headers: {
      'X-CSRF-Token': token,
      ...options.headers
    }
  };

  return fetch(url, fetchOptions).then(response => {
    if (response.status === 422 && response.headers.get('content-type')?.includes('application/json')) {
      // Check if it's a CSRF token error
      return response.json().then(data => {
        if (data.error && data.error.includes('InvalidAuthenticityToken')) {
          // Refresh token and retry once
          return refreshCSRFToken().then(() => {
            const newToken = getCSRFToken();
            if (newToken) {
              return performFetch(url, options, newToken);
            } else {
              throw new Error('CSRF token refresh failed');
            }
          });
        }
        throw new Error(data.error || 'Request failed');
      });
    }
    return response;
  });
}